import React from 'react';
import { AttackThreatActor } from '@/lib/api/threatFeedsTypes';
import { Skull } from 'lucide-react';

interface AttackThreatActorsListProps {
    // Now perfectly synced with the global Redux API type
    data?: AttackThreatActor[];
    isLoading?: boolean;
}

// Helper to convert full country names to the 2-letter codes seen in the UI
const getCountryCode = (countryName: string): string => {
    if (!countryName) return 'UN';

    const map: Record<string, string> = {
        "Russian Federation": "RU",
        "Russia": "RU",
        "China": "CN",
        "Iran": "IR",
        "North Korea": "KP",
        "United States": "US",
        "United Kingdom": "UK",
        "Ukraine": "UA",
        "Israel": "IL",
        "Netherlands": "NL",
        "Germany": "DE",
        "France": "FR"
    };

    // Return the mapped code, or fallback to the first 2 letters capitalized
    return map[countryName] || countryName.substring(0, 2).toUpperCase();
};

const AttackThreatActorsList: React.FC<AttackThreatActorsListProps> = ({ data = [], isLoading }) => {

    // Process the data: Sort by highest attack count, take the top 4
    const topActors = [...data]
        .sort((a, b) => (b.attack_count || 0) - (a.attack_count || 0))
        .slice(0, 4);

    return (
        <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg flex flex-col h-full font-mono relative overflow-hidden">

            {/* Header */}
            <div className="flex justify-between items-end mb-6">
                <h2 className="text-[14px] font-bold tracking-wide text-white">Attack Threats Actors</h2>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">
                    Top 4 • 30D
                </div>
            </div>

            {/* List Container */}
            <div className="flex flex-col gap-3 flex-1">
                {isLoading ? (
                    // Loading Skeletons
                    [...Array(4)].map((_, i) => (
                        <div key={i} className="flex items-center bg-[#13171F] rounded-xl p-4 animate-pulse">
                            <div className="w-4 h-4 bg-slate-800 rounded mr-4"></div>
                            <div className="flex-1">
                                <div className="h-3 w-24 bg-slate-800 rounded mb-2"></div>
                                <div className="h-2 w-16 bg-slate-800 rounded"></div>
                            </div>
                        </div>
                    ))
                ) : topActors.length === 0 ? (
                    // Empty State
                    <div className="flex-1 flex items-center justify-center text-xs text-slate-600 border border-dashed border-slate-800 rounded-xl">
                        No threat actors detected.
                    </div>
                ) : (
                    // Render Live Data Rows
                    topActors.map((actor, index) => (
                        <div
                            key={index}
                            className="flex items-center bg-[#10131A] hover:bg-[#161A23] transition-colors duration-200 rounded-xl px-5 py-3.5 border border-[#1A1D24]/40"
                        >
                            {/* 1. Rank Number */}
                            <span className="text-slate-500 text-xs w-6 font-semibold">
                                {index + 1}.
                            </span>

                            {/* 2. Red Skull Icon */}
                            <div className="mr-4 text-[#FF3333]">
                                <Skull className="w-[14px] h-[14px]" strokeWidth={2.5} />
                            </div>

                            {/* 3. Actor Details */}
                            <div className="flex-1 flex flex-col justify-center">
                                <span className="text-slate-200 text-sm font-medium tracking-wide mb-1 leading-none">
                                    {actor.actor_name}
                                </span>
                                <span className="text-slate-500 text-[10px] uppercase tracking-wider leading-none">
                                    {actor.victim_industry} • {getCountryCode(actor.victim_country)}
                                </span>
                            </div>

                            {/* 4. Attack Count */}
                            <div className="text-[#FF3333] text-sm font-bold tracking-wide">
                                {actor.attack_count.toLocaleString()}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default AttackThreatActorsList;